import React, { useEffect, useState, useRef } from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { newModifiedArray } from '_ui-core-js/utilities/newModifiedArray.js';
import { useEventListener } from '_ui-core-js/React/hooks/useEventListener.js';
import { TextContent } from '_ui-core-js/React/components/TextContent.jsx';
import { Button } from '_ui-core-js/React/components/Button.jsx';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { ImageSelectionGrid } from '_ui-core-js/React/components/ImageSelectionGrid.jsx';
import { StepProgressTracker } from '_ui-core-js/React/components/StepProgessTracker.jsx';
import { VIEWS } from './AiExperience.jsx';
import { fireGTMTrackingEvent } from '_ui-core-js/utilities//fireGTMTrackingEvent.js';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { LBX_GTM_TRACKING_VALUES } from '../../constants/analytics.js';

export function UserChoicesSelectionScreen(props) {

  const { block, data, state } = props;
  const [viewTransitionOut, setViewTransitionOut] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const stepLabelsRef = useRef(data.map(item => item.stepLabel));
  const imageGridContainerRef = useRef();
  const imageGridContainerHeightRef = useRef();
  const { title, subtitle, copy, ctas, progressTrackerLabel, progressTrackerPaginationDivider, images } = data[activeStep];
  const [selectedImages, setSelectedImages] = useState(state.selectedUserChoicesImages?.length ? state.selectedUserChoicesImages : data.map(() => false));
  const [selectionsComplete, setSelectionsComplete] = useState(selectedImages.indexOf(false) === -1);
  const imageSelectionMax = 1;
  const isLastStep = activeStep === data.length - 1;

  useEventListener('resize', removeGridContainerHeight);

  function removeGridContainerHeight() {
    imageGridContainerHeightRef.current = undefined;
    imageGridContainerRef.current.style.height = 'auto';
  };

  function updateImageSelection(selectedImagesArray) { 
    const selectedImage = selectedImagesArray[0];

    if (selectedImage) { // Image has not been selected for step
      const stepTrackingMap = [
        LBX_GTM_TRACKING_VALUES.label.lbxPersonality,
        LBX_GTM_TRACKING_VALUES.label.lbxArtStyle,
        LBX_GTM_TRACKING_VALUES.label.lbxTexture,
      ]

      fireGTMTrackingEvent({
        name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
        componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
        componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
        action: CORE_GTM_TRACKING_VALUES.action.addImage,
        label: stepTrackingMap[activeStep]
      });

      imageGridContainerHeightRef.current = imageGridContainerRef.current.offsetHeight;
      setSelectedImages(prevArray => newModifiedArray(prevArray, activeStep, selectedImage));

      if (!isLastStep) {
        setViewTransitionOut(true);
      } 
      else {
        setSelectionsComplete(true);
      }
    } 
    else {
      setSelectedImages(prevArray => newModifiedArray(prevArray, activeStep, false));
      setSelectionsComplete(false);
    }
  };

  function updateView() {
    setViewTransitionOut(false);
    setActiveStep(prevValue => prevValue + 1);  
  };

  function jumpToStep(index) {
    // if clicked step is completed already AND if going back to previous step OR if going forward make sure current is selected
    if (selectedImages[index] && (index < activeStep || selectedImages[activeStep])) { 
      imageGridContainerHeightRef.current = imageGridContainerRef.current.offsetHeight;
      setActiveStep(index);
    }
  };

  function goBackToColorPalettePresentation() {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.previousStep,
      label: CORE_GTM_TRACKING_VALUES.label.artworkSelectionScreen
    });

    state.setSelectedUserChoicesImages(selectedImages);
    state.setActiveView(VIEWS.colorPalettePresentation.name);
  };

  function proceedToCarColorSelection() {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.ctaEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.clickCta,
      label: CORE_GTM_TRACKING_VALUES.label.confirmArtworkSelection
    });

    state.setSelectedUserChoicesImages(selectedImages);
    state.setActiveView(VIEWS.carColorSelection.name);
  };

  useEffect(() => {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.workflowEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.workflowStep,
      label: CORE_GTM_TRACKING_VALUES.label.artworkSelectionScreen
    }, {
      workflow: {
        name: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience,
        step: CORE_GTM_TRACKING_VALUES.label.artworkSelectionScreen,
        conversion: 0,
        variant: (state.loggedIn && state.instagramCode) ? 'instagram' : 'not-connected'
      }
    });
  }, []);

  return (
    <div className={`${block}__logged-out-image-selection ${block}__layout--horizontal container-xxxl`}>
      <div className='row'>
        <div className={`${block}__layout--block-one col-12 col-md-5 col-lg-4`}>
          <TextContent 
          heading={title}
          subheading={subtitle}
          body={copy}
          modifier='centered-mobile'
		  cssAnimationTrigger={true}
          />
          <StepProgressTracker 
          steps={data.length}
          currentStep={activeStep}
          label={progressTrackerLabel}
          stepLabels={stepLabelsRef.current}
          showPagination={true}
          paginationDivider={progressTrackerPaginationDivider}
          autoCompleteOverride={selectedImages.map(image => !!image)}
          verticalLayout={true}
          cssAnimationDelay={0.5}
          onClickCallback={index => jumpToStep(index)}
          />
          <div className={`${block}__cta-container`}>
            <Button 
            label={ctas.next.label} 
            type='primary' 
            cssAnimation={true}
            cssAnimationName='vertical-top-bottom-clip'
            cssAnimationTrigger={true}
            cssAnimationDuration={0.5}
            cssAnimationDelay={1} 
            disabled={!selectionsComplete}
            onClickCallback={proceedToCarColorSelection}
            />
            <Button 
            label={ctas.back.label} 
            type='tertiary' 
            icon='back'
            slim={true}
            cssAnimation={true}
            cssAnimationTrigger={true}
            cssAnimationDelay={1.5}
            onClickCallback={goBackToColorPalettePresentation}
            />
          </div>
        </div>
        <div className={`${block}__layout--block-two col-12 col-md-6 offset-md-1 offset-lg-2`}>
          <CssAnimation
          exit={{ name: 'fade-out', duration: 0.35 }}
          triggerExit={viewTransitionOut}
          onExitEnd={updateView}
          >
            <div className={classNames({
              [`${block}__image-selection-grid-container`] : true,
              [`${block}__image-selection-grid-container--disable`] : viewTransitionOut
            })}
            ref={imageGridContainerRef}
            style={{ height: imageGridContainerHeightRef.current ? `${imageGridContainerHeightRef.current}px` : 'auto' }}
            >
              <ImageSelectionGrid 
              images={images} 
              loading='eager'
              initSelected={selectedImages[activeStep] ? [selectedImages[activeStep].index] : undefined} // if has selected - create array from value
              onSelectCallback={selectedImagesArray => updateImageSelection(selectedImagesArray)}
              maxSelection={imageSelectionMax}
              key={activeStep}
              />
            </div>
          </CssAnimation>
        </div> 
      </div>
    </div>
  )
};

UserChoicesSelectionScreen.propTypes = {
  block: propTypes.string.isRequired,
  state: propTypes.object.isRequired,
  data: propTypes.oneOfType([
    propTypes.array,
    propTypes.object
  ]).isRequired
};

